import type { BrokerCatalogItem } from "../services/brokers.service";

/**
 * A broker insurance quote in any of its shapes: BrokerQuote (quoter-v2 full_quote)
 * or the persisted insurance_response element. Fields vary per broker, so it is
 * handled as a loose record.
 */
export type BrokerQuoteRecord = Record<string, unknown> | null | undefined;

/**
 * Candidate folio fields per broker, in resolution priority order. Covers both the
 * quote-time naming (`*_quote_id`) and the persisted naming (`*_solicitud_id`).
 * howden uses the broker's own quote id (`cotizacion_id` family), not the
 * insurer's `num_cotizacion_ase` (kept only as a last-resort fallback below).
 */
const BROKER_FOLIO_FIELDS: { [broker: string]: string[] } = {
  howden: ["ordas_quote_id", "ordas_solicitud_id", "ordas_id_cotizacion", "cotizacion_id"],
  inter: ["inter_quote_id", "inter_solicitud_id"],
  interesse: ["interesse_id_cotizacion"],
  eikos: ["eikos_id_cotizacion"],
  marsh: ["marsh_id_cotizacion"],
};

/** Broker detection order when the quote has no explicit broker field. */
const BROKER_DETECTION_ORDER: string[] = ["marsh", "eikos", "inter", "interesse", "howden"];

/** Fallback display names when the broker catalog is unavailable or has no match. */
const FALLBACK_BROKER_NAMES: { [broker: string]: string } = {
  howden: "Howden",
  inter: "Inter",
  eikos: "Eikos",
  interesse: "Interesse",
  marsh: "Marsh",
};

/**
 * Shared resolution/formatting of the insurance quote folio (DEV-9174).
 *
 * "Base folio" is the raw broker quote id and must never be altered (it is the id
 * used for policy emission). The "display folio" prepends the broker prefix from
 * the backend catalog (`{prefix}-{folio}`) and exists only at presentation level:
 * it is never persisted nor sent in print payloads (the backend prefixes PDFs).
 */
export class InsuranceFolioUtilities {
  /**
   * Canonical broker slug for a quote. Prefers the explicit broker value and falls
   * back to detecting which broker-specific id fields are present.
   */
  static resolveBrokerKey(quote: BrokerQuoteRecord, explicitBroker?: string | null): string {
    const fromExplicit = String(explicitBroker || quote?.["broker"] || "").toLowerCase();
    if (Object.prototype.hasOwnProperty.call(BROKER_FOLIO_FIELDS, fromExplicit)) {
      return fromExplicit;
    }
    if (!quote) {
      return "";
    }
    for (const broker of BROKER_DETECTION_ORDER) {
      if (BROKER_FOLIO_FIELDS[broker].some((field) => quote[field] != null && quote[field] !== "")) {
        return broker;
      }
    }
    return "";
  }

  /** Base folio (raw broker quote id) or "" when it cannot be resolved. */
  static resolveQuoteFolio(quote: BrokerQuoteRecord, explicitBroker?: string | null): string {
    if (!quote) {
      return "";
    }

    const brokerKey = this.resolveBrokerKey(quote, explicitBroker);
    const candidateFields = brokerKey
      ? BROKER_FOLIO_FIELDS[brokerKey]
      : BROKER_DETECTION_ORDER.reduce<string[]>((fields, broker) => fields.concat(BROKER_FOLIO_FIELDS[broker]), []);

    for (const field of candidateFields) {
      const value = quote[field];
      if (value != null && value !== "") {
        return String(value);
      }
    }

    return this.resolveFolioFromCompleteResponses(quote, brokerKey);
  }

  /** Display folio: `{prefix}-{folio}`, or the base folio when no prefix is configured. */
  static formatDisplayFolio(folio: string, prefix?: string | null): string {
    if (!folio) {
      return "";
    }
    return prefix ? `${prefix}-${folio}` : folio;
  }

  /** Convenience: resolve the base folio and apply the catalog prefix in one step. */
  static getDisplayFolio(
    quote: BrokerQuoteRecord,
    explicitBroker: string | null | undefined,
    catalog: BrokerCatalogItem[]
  ): string {
    const folio = this.resolveQuoteFolio(quote, explicitBroker);
    const broker = this.findCatalogBroker(this.resolveBrokerKey(quote, explicitBroker), catalog);
    return this.formatDisplayFolio(folio, broker?.prefix);
  }

  /** Catalog entry matching a broker slug, or null when not found. */
  static findCatalogBroker(brokerKey: string, catalog: BrokerCatalogItem[]): BrokerCatalogItem | null {
    if (!brokerKey) {
      return null;
    }
    return catalog.find((broker) => String(broker.key || "").toLowerCase() === brokerKey) || null;
  }

  /**
   * Full broker name for tooltips: catalog name when available, otherwise the
   * static fallback label. Returns "" for unknown brokers so the UI never shows
   * a fabricated value.
   */
  static getBrokerDisplayName(brokerKey: string | null | undefined, catalog: BrokerCatalogItem[]): string {
    const key = String(brokerKey || "").toLowerCase();
    if (!key) {
      return "";
    }
    const catalogBroker = this.findCatalogBroker(key, catalog);
    return catalogBroker?.name || FALLBACK_BROKER_NAMES[key] || "";
  }

  /** Last-resort folio lookup inside the raw broker responses embedded in the quote. */
  private static resolveFolioFromCompleteResponses(quote: Record<string, unknown>, brokerKey: string): string {
    const howden = quote["complete_response_howden"] as
      | { cotizacion_id?: string; num_cotizacion_ase?: string }
      | undefined;
    const inter = quote["complete_response_inter"] as { cotizacionID?: number } | undefined;
    const interesse = quote["complete_response_interesse"] as { quote?: { InsuranceQuoteId?: number } } | undefined;
    const eikos = quote["complete_response_eikos"] as { ID_COTIZACION?: number | string } | undefined;
    const marsh = quote["complete_response_marsh"] as { quote_id?: number } | undefined;

    const byBroker: { [broker: string]: unknown } = {
      howden: howden?.cotizacion_id ?? howden?.num_cotizacion_ase,
      inter: inter?.cotizacionID,
      interesse: interesse?.quote?.InsuranceQuoteId,
      eikos: eikos?.ID_COTIZACION,
      marsh: marsh?.quote_id,
    };

    const value = brokerKey
      ? byBroker[brokerKey]
      : Object.values(byBroker).find((candidate) => candidate != null && candidate !== "");
    return value != null && value !== "" ? String(value) : "";
  }
}
